// notificationModules.js
//
// Konfigurasi terpusat untuk setiap kategori notifikasi (Izin, Cuti, FPB, dst).
// Sebelumnya setiap kategori punya blok template ~150-200 baris yang
// diduplikasi manual (copy-paste) di Notifications.vue, sehingga total
// jadi ~1.100 baris hanya untuk 7 kartu yang bentuknya nyaris sama.
//
// Sekarang: menambah / mengubah kategori cukup di sini, satu tempat.
// Template (Notifications.vue + NotificationCard.vue) generik dan tidak
// perlu disentuh lagi ketika ada kategori baru.
//
// tabIndex: 0 dipakai NOTIF_MODULE ("Notifikasi" — daftar dari
// loadNotification/getNotification, tab default saat halaman dibuka).
// tabIndex: 1 dipakai ALL_MODULE ("Semua Pengajuan" — gabungan client-side
// dari 7 kategori di bawah, tetap dipertahankan sebagai pembanding di
// samping "Notifikasi"). 7 kategori nyata diberi tabIndex 2-8. Pemilihan
// kategori di sidebar & bottom sheet memakai binding `value` eksplisit
// (bukan posisi urutan elemen), jadi urutan array bebas ditata ulang
// tanpa memengaruhi tab mana yang aktif.

export const NOTIF_MODULE = {
  key: 'notification',
  tabIndex: 0,
  label: 'Notifikasi',
  listLabel: 'Semua Notifikasi',
  icon: 'mdi-bell-outline',
  avatarText: 'NTF',
  avatarColor: 'rgb(70, 130, 180)',
  borderClass: '',
  // Field dikonfirmasi dari contoh response asli endpoint
  // notification/list: { subject, body, module, document_id, status, ... }
  // — subject = judul notifikasi, body = deskripsi/nomor dokumennya.
  getTitle: (item) => item.subject || '-',
  getSubtitle: (item) => item.body || '-',
  // status di sini adalah status BACA (0 = belum dibaca), BUKAN status
  // approval seperti 7 kategori lain — field ini dipastikan dari
  // changeToRead di store (`if (payload.status === 0)`) dan juga cocok
  // dengan contoh data (semua item pending punya status: 0).
  getStatusDocument: (item) =>
    item.status === 0 ? 'Belum Dibaca' : 'Sudah Dibaca',
  getStatusColorDocument: (item) => (item.status === 0 ? 'red' : '#05668d'),
  getStatusLabel: (item, statusName) =>
    statusName(item.document_status, item.module),
  getStatusColor: (item, statusColor) =>
    statusColor(item.document_status, item.module),
  ribbon: null
}

export const ALL_MODULE = {
  key: 'all',
  tabIndex: 1,
  label: 'Semua',
  listLabel: 'Semua Pengajuan',
  icon: 'mdi-view-dashboard-outline',
  // Dipakai NotificationCard hanya sebagai fallback kalau suatu saat ada
  // item gabungan yang gagal ditandai modul asalnya — dalam kondisi
  // normal, setiap kartu di tab "Semua" tetap pakai avatar/warna modul
  // aslinya masing-masing (lihat cardItems() di Notifications.vue).
  avatarText: 'ALL',
  avatarColor: 'rgb(120, 120, 120)',
  borderClass: ''
}

export const NOTIFICATION_MODULES = [
  {
    key: 'permission',
    tabIndex: 2,
    label: 'Izin',
    listLabel: 'Pengajuan Izin',
    icon: 'mdi-file-document',
    avatarText: 'IZN',
    avatarColor: 'rgb(94, 94, 213)',
    borderClass: 'blue-background',
    getter: 'getPermissionApprovalNeeds',
    action: 'loadPermissionApprovalNeeds',
    openModuleValue: 'permission',
    getTitle: (item) => item.employee && item.employee.name,
    getSubtitle: (item) => item.request_no,
    getStatusId: (item) => item.status_permission,
    getStatusLabel: (item, statusName) =>
      statusName(item.status_permission, 'permission'),
    getStatusColor: (item, statusColor) =>
      statusColor(item.status_permission, 'permission'),
    ribbon: (item, statusName) => ({
      color: item.status_permission === 1 ? 'red' : '#83c5be',
      text:
        item.status_permission === 1
          ? 'WAITING'
          : statusName(item.status_permission, 'permission').toUpperCase()
    })
  },
  {
    key: 'leave',
    tabIndex: 3,
    label: 'Cuti',
    listLabel: 'Pengajuan Cuti',
    icon: 'mdi-calendar-plus',
    avatarText: 'CTI',
    avatarColor: 'rgb(218, 218, 102)',
    borderClass: 'yellow-background',
    getter: 'getLeaveApprovalNeeds',
    action: 'loadLeaveApprovalNeeds',
    openModuleValue: 'leave',
    getTitle: (item) => item.employee && item.employee.name,
    getSubtitle: (item) => item.request_no,
    getStatusId: (item) => item.status_leave,
    getStatusLabel: (item, statusName) =>
      statusName(item.status_leave, 'leave'),
    getStatusColor: (item, statusColor) =>
      statusColor(item.status_leave, 'leave'),
    // Catatan: di file asli, teks ribbon tab Cuti memanggil
    // statusName(item.status_leave, 'permission') — module salah ketik,
    // seharusnya 'leave'. Sudah dibetulkan di bawah ini.
    ribbon: (item, statusName) => ({
      color: item.status_leave === 1 ? 'red' : '#83c5be',
      text:
        item.status_leave === 1
          ? 'WAITING'
          : statusName(item.status_leave, 'leave').toUpperCase()
    })
  },
  {
    key: 'eCatalogue',
    tabIndex: 4,
    label: 'FPB',
    listLabel: 'Pengajuan FPB',
    icon: 'mdi-clipboard-list-outline',
    avatarText: 'FPB',
    avatarColor: 'rgb(60, 194, 132)',
    borderClass: 'green-background',
    getter: 'getFpbApprovalNeeds',
    action: 'loadFpbApprovalNeeds',
    openModuleValue: 'eCatalogue',
    getTitle: (item) => item.employee && item.employee.name,
    getSubtitle: (item) => item.request_no,
    getStatusId: (item) => (item.status != null ? item.status.id : null),
    getStatusLabel: (item) =>
      item.status != null && item.status.name
        ? item.status.name.toUpperCase()
        : '-',
    getStatusColor: (item, statusColor) =>
      statusColor(item.status != null ? item.status.id : null, 'eCatalogue'),
    ribbon: null
  },
  {
    key: 'rab',
    tabIndex: 5,
    label: 'RAB',
    listLabel: 'Pengajuan RAB',
    icon: 'mdi-clipboard-list-outline',
    avatarText: 'RAB',
    avatarColor: 'rgb(60, 194, 132)',
    borderClass: 'green-background',
    getter: 'getRabApprovalNeeds',
    action: 'loadRabApprovalNeeds',
    openModuleValue: 'rab',
    getTitle: (item) => item.employee && item.employee.name,
    getSubtitle: (item) => item.budget_no,
    getStatusId: (item) => (item.status != null ? item.status.id : null),
    getStatusLabel: (item) =>
      item.status != null && item.status.name
        ? item.status.name.toUpperCase()
        : '-',
    getStatusColor: (item, statusColor) =>
      statusColor(item.status != null ? item.status.id : null, 'eCatalogue'),
    ribbon: null
  },
  {
    key: 'ca',
    tabIndex: 6,
    label: 'Payreq/CA',
    listLabel: 'Pengajuan CA',
    icon: 'mdi-clipboard-list-outline',
    avatarText: 'CA',
    avatarColor: 'rgb(60, 194, 132)',
    borderClass: 'green-background',
    getter: 'getCaApprovalNeeds',
    action: 'loadCaApprovalNeeds',
    openModuleValue: 'Cash Advance',
    getTitle: (item) => item.employee_name,
    getSubtitle: (item) => item.document_no,
    // PERHATIAN — inkonsistensi yang sudah ada di kode asli, dipertahankan
    // apa adanya karena saya tidak tahu bentuk data `item.status` yang
    // sebenarnya dari API: getStatusColor mengasumsikan item.status adalah
    // OBJECT ({id, name}), tapi getStatusLabel mengasumsikan item.status
    // adalah NUMBER (dipakai sbg index array di statusName). Salah satunya
    // kemungkinan besar tidak pernah menghasilkan warna/label yang benar.
    // Perlu dicek ke tim backend/API sebelum diperbaiki, supaya tidak
    // menebak yang salah.
    getStatusId: (item) => (item.status != null ? item.status.id : null),
    getStatusLabel: (item, statusName) =>
      statusName(item.status, 'Cash Advance'),
    getStatusColor: (item, statusColor) =>
      statusColor(item.status != null ? item.status.id : null, 'eCatalogue'),
    ribbon: null
  },
  {
    key: 'pjca',
    tabIndex: 7,
    label: 'PJCA',
    listLabel: 'Pengajuan PJCA',
    icon: 'mdi-clipboard-list-outline',
    avatarText: 'PJCA',
    avatarColor: 'rgb(60, 194, 132)',
    borderClass: 'green-background',
    getter: 'getPjcaApprovalNeeds',
    action: 'loadPjcaApprovalNeeds',
    openModuleValue: 'pjca',
    getTitle: (item) => item.employee_name,
    getSubtitle: (item) => item.document_no,
    // Sama seperti catatan pada modul CA di atas.
    getStatusId: (item) => (item.status != null ? item.status.id : null),
    getStatusLabel: (item, statusName) => statusName(item.status, 'pjca'),
    getStatusColor: (item, statusColor) =>
      statusColor(item.status != null ? item.status.id : null, 'eCatalogue'),
    ribbon: null
  },
  {
    key: 'overtime',
    tabIndex: 8,
    label: 'Lembur',
    listLabel: 'Pengajuan Lembur',
    icon: 'mdi-clipboard-list-outline',
    avatarText: 'OVT',
    avatarColor: 'rgb(60, 194, 132)',
    borderClass: 'green-background',
    getter: 'getOvertimeApprovalNeeds',
    action: 'loadOvertimeApprovalNeeds',
    openModuleValue: 'overtime',
    getTitle: (item) => item.created_by_name,
    getSubtitle: (item) => item.document_no,
    // Sama seperti catatan pada modul CA di atas.
    getStatusId: (item) => (item.status != null ? item.status.id : null),
    getStatusLabel: (item, statusName) => statusName(item.status, 'overtime'),
    getStatusColor: (item, statusColor) =>
      statusColor(item.status != null ? item.status.id : null, 'eCatalogue'),
    ribbon: null
  }
]

export const CATEGORY_FILTER_OPTIONS = [
  { id: 'leave', name: 'Cuti' },
  { id: 'permission', name: 'Izin' },
  { id: 'ecatalogue', name: 'FPB' },
  { id: 'ca', name: 'CA' },
  { id: 'pjca', name: 'PJCA' },
  { id: 'overtime', name: 'Lembur' }
]

export const STATUS_FILTER_OPTIONS = [
  'All',
  'Draft',
  'Waiting Approval',
  'Canceled',
  'Rejected',
  'Approved'
]
